import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component, Inject, OnDestroy, OnInit,
  Renderer2
} from '@angular/core';
import {
  ActivatedRoute,
  ParamMap,
  Params,
  Router
} from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  map,
  Observable,
  of,
  Subject, Subscription,
  take,
  takeUntil, tap
} from 'rxjs';

import { USStateAbbrevMap } from '@sondermind/constants';
import { PublicFooterModule } from '@sondermind/features/app-footer';
import { PublicHeaderModule } from '@sondermind/features/app-header';
import {
  EmptyURIValue,
  FilterFormComponent, FilterOptionsStore, fixedEncodeURIComponent, IConcernOptionValue, IFilterFormValues,
  ISeoElements,
  IUrlParamValues,
  ParseDirectoryFilterParams,
  UriDelimiter,
  URIPrefix,
  UrlQueryParams
} from '@sondermind/features/filter-form';
import {
  IProviderListingCardSegmentSchema,
  IProviderPreview,
  IProviderSoonestAvailability,
  IProviderSoonestAvailabilityApiListing,
  ProviderDirectoryPageViewEvent,
  ProviderListingCardComponent,
  ProviderListingCardEvents
} from '@sondermind/features/provider-listing-card';
import { GtmDataLayerService } from '@sondermind/google-tag-manager';
import { IrisModalService, IrisModalSize } from '@sondermind/iris/modal';
import { ENVIRONMENT } from '@sondermind/utilities/models-environment';
import { IAPIFilterOption, IAPIListRequestOptions, IAPIListResponse } from '@sondermind/utilities/models-http';
import { PipesModule } from '@sondermind/utilities/pipes';
import { BreakpointsService } from '@sondermind/utilities/services';
import { WINDOW } from '@sondermind/utilities/tools';

import { Meta, Title } from '@angular/platform-browser';
import { ConfigurationService, IDirectoryAppConfig } from '@sondermind/configuration';
import { LAUNCH_DARKLY_SERVICE, LaunchDarklyService, LDBooleanFeatureFlags } from '@sondermind/launch-darkly';
import { SegmentService } from '@sondermind/segment';
import { environment } from '../../../environments/environment';
import { ProviderSchema } from '../../models/provider-schema';
import { ProviderDirectoryHttpService } from '../../services/provider-directory.httpservice';
import { FiltersErrorComponent } from '../directory-error-states/filters-error/filters-error.component';
import { GeneralErrorComponent } from '../directory-error-states/general-error/general-error.component';
import { LocationNeededComponent } from '../directory-error-states/location-needed/location-needed.component';
import { NoResultsStateComponent } from '../directory-error-states/no-results/no-results.component';
import { FAQDropdownComponent } from '../faq-dropdown/faq-dropdown.component';

const PROVIDER_REQUEST_PAGE_SIZE = 15;
const SOONEST_AVAILABILITY_REQUEST_LIMIT_COUNT: number = 10;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FAQDropdownComponent,
    FilterFormComponent,
    FiltersErrorComponent,
    GeneralErrorComponent,
    LocationNeededComponent,
    NoResultsStateComponent,
    PipesModule,
    ProviderListingCardComponent,
    PublicFooterModule,
    PublicHeaderModule
  ],
  selector: 'directory-page',
  providers: [
    { provide: ENVIRONMENT, useValue: environment }
  ],
  templateUrl: './directory-page.component.html',
  styleUrls: ['./directory-page.component.scss'],
})
export class DirectoryPage implements OnDestroy, OnInit {
  // Initial results shows the loading message at the top of the screen, when fetching with a brand new filter set
  // Additional results shows the loading message at the bottom of the screen, when requesting more results
  loadingInitialResults: boolean = true;
  loadingAdditionalResults: boolean = false;

  currentApiFilters: IAPIFilterOption[];
  currentFormFilters: Partial<IFilterFormValues>;

  locationFilterApplied: boolean = false;

  results$: BehaviorSubject<IProviderPreview[]> = new BehaviorSubject<IProviderPreview[]>([]);
  currentResultsPage: number = 0;
  therapistRequestSub: Subscription;

  totalProviderCount: number = 0;

  selectedState: string = '';
  selectedStateAbbrev: string = '';
  showErrorMessageFiltersRetrieval: boolean = false;
  showErrorMessageResultsRetrieval: boolean = false;
  showProviderCountMessage: boolean = false;
  reachedFinalProviderResultPage: boolean = false;

  destroyed$ = new Subject<void>();
  paramMap$: Observable<ParamMap>;
  queryParams$: Observable<Params>;
  queryParamValue: Params;
  skeletonCards: unknown[] = Array(3);
  locationModalOpen = false;

  matchflowBaseUrl: string = '';

  providersAvailabilitySubject = new BehaviorSubject<{ [personaProviderId: number]: IProviderSoonestAvailability; }>({});
  providersSoonestAvailability$ = this.providersAvailabilitySubject.asObservable();

  constructor(
    private providerDirectoryHttpService: ProviderDirectoryHttpService,
    private route: ActivatedRoute,
    public breakpointService: BreakpointsService,
    private irisModalService: IrisModalService,
    public filterOptionsStore: FilterOptionsStore,
    private gtmDataLayerService: GtmDataLayerService,
    private configurationService: ConfigurationService<IDirectoryAppConfig>,
    private metaService: Meta,
    private titleService: Title,
    private segmentService: SegmentService<IProviderListingCardSegmentSchema>,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    @Inject(WINDOW) private window: Window,
    private renderer2: Renderer2,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: LaunchDarklyService,
  ) { }

  ngOnInit(): void {
    this.matchflowBaseUrl = this.configurationService.env.app.flowHost;
    this.loadingInitialResults = true;
    this.queryParams$ = this.route.queryParams;
    this.paramMap$ = this.route.paramMap;

    combineLatest([
      this.filterOptionsStore.isStateLoaded$(),
      this.filterOptionsStore.currentState$(),
      this.route.paramMap,
      this.route.queryParams
    ]).pipe(
      debounceTime(100), // This is necessary to avoid failing to detect queryParams when they do exist
    ).subscribe(([isLoaded, savedState, paramMap, queryParams]) => {
      this.queryParamValue = queryParams;
      const stateParam: string | null = paramMap.get('state')?.split('-')[1]?.toUpperCase() ?? null;
      if ((stateParam && stateParam !== EmptyURIValue.toLocaleUpperCase())
          || (isLoaded && savedState && savedState !== '' && savedState !== EmptyURIValue.toLocaleUpperCase())) {
        this.locationFilterApplied = true;
      } else {
        // When there is both no state query param and no state saved in local storage
        this.openLocationModal();
      }
    });

    this.prepForFullScreenModals();

    this.breakpointService.isMobile$.pipe(
      tap((isMobile) => {
        // unset the onscroll function when not on mobile
        this.window.onscroll = isMobile ? this.onScrollCollapseHeader : () => {};
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  onScrollCollapseHeader(): void {
    if (this.window.scrollY > 80) {
      document.getElementById('header-title').className = 'find-a-therapist header-contents header-title no-padding';
      document.getElementById('header-filters').className = 'header-contents filters collapsed';
      const headerCount = document.getElementById('header-count');
      if (headerCount) {
        headerCount.className = 'available-provider-count header-contents no-padding';
      }
    } else {
      document.getElementById('header-title').className = 'find-a-therapist header-contents header-title';
      document.getElementById('header-filters').className = 'header-contents filters';
      const headerCount = document.getElementById('header-count');
      if (headerCount) {
        headerCount.className = 'available-provider-count header-contents';
      }
    }
  }

  /** When the BreakpointsService detects that a full screen modal has opened,
   * remove the scrollbar for the contents behind it.
   * Put it back in when the modal is closed. */
  private prepForFullScreenModals(): void {
    this.breakpointService.isModal$.pipe(
      tap((isModalOpen) => {
        document.body.style.overflow = isModalOpen ? 'hidden' : 'auto';
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  openLocationModal(): void {
    if (this.locationModalOpen) return;
    this.locationModalOpen = true;
    this.breakpointService.toggleModal(true);
    const modalRef = this.irisModalService.open<LocationNeededComponent, null, string>(
      LocationNeededComponent,
      {
        size: IrisModalSize.MD,
      }
    );

    modalRef.afterClosed$().subscribe((selectedState: string) => {
      this.locationModalOpen = false;
      this.filterOptionsStore.setCurrentState(selectedState);
      this.breakpointService.toggleModal(false);
    });
  }

  requestTherapists(): void {
    const filters = this.currentApiFilters;
    if (filters.length > 0 && !this.reachedFinalProviderResultPage) {
      const options: IAPIListRequestOptions = {
        filter: filters,
        page: {
          number: this.currentResultsPage + 1,
          size: PROVIDER_REQUEST_PAGE_SIZE,
        }
      };

      this.showErrorMessageResultsRetrieval = false;
      // Initial is only true once, additional needs to be true every time after
      this.loadingInitialResults = this.currentResultsPage === 0;
      this.loadingAdditionalResults = this.currentResultsPage > 0;

      // When requesting additional results, we want to show only one skeleton card
      this.skeletonCards = this.loadingAdditionalResults ? Array(1) : Array(3);

      if (this.therapistRequestSub) {
        // Cancel existing/ongoing requests
        this.therapistRequestSub.unsubscribe();
      }

      this.therapistRequestSub = this.providerDirectoryHttpService.list$(options).pipe(
        catchError((error) => {
          this.showErrorMessageResultsRetrieval = true;
          this.loadingInitialResults = false;
          return of(error);
        })
      ).subscribe((response: IAPIListResponse<IProviderPreview>) => {
        if (response.data) {
          this.loadingInitialResults = false;
          this.loadingAdditionalResults = false;

          this.showProviderCountMessage = true;
          this.totalProviderCount = response.meta.counts.filtered;

          this.currentResultsPage = response.meta.page.number;
          this.reachedFinalProviderResultPage = this.checkIfLastPage(response);

          if (this.currentResultsPage === 1) {
            // Track what filters we are using when a user enters a new filter set,
            // and how many results are returned
            this.gtmDataLayerService.sendNewDirectoryProviderSetLoadedEvent(
              filters,
              response.meta.counts.filtered
            );

            // Ensure results list is empty to begin with
            this.results$.next([]);
          } else { // otherwise, send an event indicating user requested more providers of the same filter set
            this.gtmDataLayerService.sendShowMoreProvidersEvent(
              filters,
              this.reachedFinalProviderResultPage
            );
          }

          const providerPreviewData: IProviderPreview[] = response.data;

          this.updateXMLSchema(providerPreviewData);
          // concatenate results
          const allResults = this.results$.getValue().concat(providerPreviewData);
          this.results$.next(allResults);

          this.launchDarklyService.checkFlagEnabled$(LDBooleanFeatureFlags.PDP_ENABLE_SOONEST_AVAILABILITY).pipe(
            takeUntil(this.destroyed$)
          ).subscribe((soonestAvailabilityFeatureFlag) => {
            if (soonestAvailabilityFeatureFlag) {
              // take newly returned provider ids and request soonest availability
              const providerIds = providerPreviewData.map((provider: IProviderPreview) => provider.id);
              this.requestAvailabilityForProviders(providerIds);
            }
          });

          this.sendDirectoryPageSegmentEvent();
        } else {
          this.showErrorMessageResultsRetrieval = true;
        }
      });
    }
  }

  onFiltersChanged(filterOptions: IAPIFilterOption[]): void {
    // reset the results
    this.results$.next([]);
    this.currentResultsPage = 0;
    this.reachedFinalProviderResultPage = false;

    // Scroll back to the top of the page if we're not already there
    if (document.body.scrollTop > 20 || document.documentElement.scrollTop > 20) {
      document.body.scrollTop = 0; // safari
      document.documentElement.scrollTop = 0;
    }

    this.showProviderCountMessage = false;

    // state is required -- if there is no state value, show the location required message
    const selectedState = filterOptions.find((f: IAPIFilterOption) => f.key === 'practice_state');
    if (selectedState) {
      this.locationFilterApplied = true;
      this.currentApiFilters = filterOptions;
      this.filterOptionsStore.setCurrentState(selectedState.value);
      this.requestTherapists();
    } else {
      // will toggle the location filter needed
      this.loadingInitialResults = false;
      this.locationFilterApplied = false;
    }
  }

  onFormValuesChanged(formValues: Partial<IFilterFormValues>): void {
    this.currentFormFilters = formValues;
    if (formValues?.state) {
      this.handleStateName(formValues.state);
    }
  }

  onFilterRetrievalError(showError: boolean): void {
    this.showErrorMessageFiltersRetrieval = showError;
  }

  private handleStateName(state: string): void {
    this.selectedState = USStateAbbrevMap[state] ?? state;
    this.selectedStateAbbrev = state;
  }

  private checkIfLastPage(response: IAPIListResponse<IProviderPreview>): boolean {
    return this.currentResultsPage === response.meta.counts.pages;
  }

  updateXMLSchema(providers: IProviderPreview[]) {
    const providerSchema: ProviderSchema[] = providers.map((provider) => ({
      '@type': 'Person',
      name: provider.display_name,
      jobTitle: Array.from(new Set(provider.certifications?.map((c) => c.name))),
      worksFor: {
        '@type': 'Organization',
        name: 'SonderMind'
      },
      url: provider.url,
      workLocation: provider.locations?.map((loc) => ({
        '@type': 'Place',
        name: loc.name,
        address: {
          '@type': 'PostalAddress',
          addressLocality: loc.city,
          addressRegion: loc.state,
          postalCode: loc.postalCode,
          addressCountry: 'US'
        }
      }))
    }));
    const element = this.document.querySelector('script[type="application/ld+json"]');
    if (element) {
      element.parentElement.removeChild(element);
    }
    const script = this.renderer2.createElement('script');
    script.type = 'application/ld+json';
    script.text = `
    {
      "@context": "https://schema.org/",
      "@graph": ${JSON.stringify(providerSchema)}
    }
    `;
    this.renderer2.appendChild(this.document.body, script);
  }
  onUpdateDynamicSeo(event: ISeoElements) {
    this.setDynamicMetaTags(event.filterFormValues);
    this.updateUrl(event);
    // this.updateCanonicalLink(event);
  }

  setDynamicMetaTags(formValues: Partial<IFilterFormValues>): void {
    let modalities: IConcernOptionValue[] = [];
    let concerns: IConcernOptionValue[] = [];
    if (formValues.concerns?.length) {
      modalities = formValues.concerns.filter((concern) => concern.isModality);
      concerns = formValues.concerns.filter((concern) => !concern.isModality);
    }
    let title = 'Find';
    let description = 'Discover';
    const modalityTitle = modalities.length > 0 ? ` ${modalities[0].title} Therapists` : ' Therapists';
    const modalityDescription = modalities.length > 0 ? ` ${modalities[0].title} Therapists` : ' Therapists';

    let stateTitle = '';
    let stateDescripton = '';
    if (formValues?.state) {
      if (formValues?.state === 'CO') {
        stateTitle = stateTitle.concat(' ', '& Psychologists');
      }
      stateTitle = stateTitle.concat(' ', `in ${USStateAbbrevMap[formValues.state]}`);
      stateDescripton = stateDescripton.concat(' ', `in ${USStateAbbrevMap[formValues.state]}`);
    }
    let concernTitle = '';
    let concernDescripton = '';
    if (concerns.length > 0) {
      const concernList = concerns.map((c) => c.title);
      // creates comma list ['a', 'b', 'c'] => 'a, b, and c' | ['a', 'b'] => 'a and b' | ['a'] => 'a'
      const concernString = concernList.join(', ').replace(/, ([^,]*)$/, ' and $1');
      concernTitle = concernTitle.concat(' ', `who specialize in ${concernString}`);
      concernDescripton = concernDescripton.concat(' ', `who specialize in ${concernString}`);
    }
    let insuranceTitle = '';
    let insuranceDescription = '';

    if (formValues?.insurance?.title) {
      insuranceTitle = insuranceTitle.concat(' ', `that accept ${formValues?.insurance?.title}`);
      insuranceDescription = insuranceDescription.concat(' ', `that accept ${formValues?.insurance?.title} near you.`);
    } else {
      insuranceDescription = insuranceDescription.concat(' ', `that offer online and in-person therapy services near you.`);
    }
    title = title.concat(modalityTitle, stateTitle, concernTitle, insuranceTitle);
    description = description.concat(modalityDescription, stateDescripton,
      concernDescripton, insuranceDescription, ' ', 'Finding the right therapist has never been easier.');
    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'title', content: title });
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ property: 'og:url', content: this.window.location.href });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'twitter:url', content: this.window.location.href });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
  }
  // TODO: This needs to be updated once we find a solution.
  private updateCanonicalLink(event: ISeoElements): void {
    const obj = this.getValidUrlObj(event);
    const providerDirectoryRoot = this.configurationService.env.providerDirectoryBase
    ?? 'https://sondermind.com/provider-directory';

    let canonicalTag = `${providerDirectoryRoot}`;
    // State should ALWAYS exist. If it doesn't, we have a bug.
    canonicalTag = `${canonicalTag}/${UrlQueryParams.STATE}/${obj.state}`;

    canonicalTag = `${canonicalTag}/${UrlQueryParams.INSURANCE}/${obj.insurance || EmptyURIValue}`;

    canonicalTag = `${canonicalTag}/${UrlQueryParams.MODALITIES}/${obj.modalities || EmptyURIValue}`;

    canonicalTag = `${canonicalTag}/${UrlQueryParams.SPECIALTIES}/${obj.specialties || EmptyURIValue}`;

    canonicalTag = `${canonicalTag}/${UrlQueryParams.GENDERS}/${obj.genders || EmptyURIValue}`;

    let link = this.document.querySelector("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalTag);
  }
  private updateUrl(event: ISeoElements): void {
    const urlObj = this.getValidUrlObj(event);
    let filterObj = '';
    if (urlObj.insurance) {
      filterObj = `${filterObj}${urlObj.insurance}${UriDelimiter}`;
    }
    if (urlObj.modalities) {
      filterObj = `${filterObj}${urlObj.modalities}${UriDelimiter}`;
    }
    if (urlObj.specialties) {
      filterObj = `${filterObj}${urlObj.specialties}${UriDelimiter}`;
    }
    if (urlObj.genders) {
      filterObj = `${filterObj}${urlObj.genders}${UriDelimiter}`;
    }
    of(this.router.navigate([
      urlObj.state || EmptyURIValue,
      urlObj.city || EmptyURIValue,
      filterObj.slice(0, -1)
    ], { queryParams: this.queryParamValue ? this.queryParamValue : {} })).subscribe();
  }

  private getValidUrlObj(event: ISeoElements): Partial<IUrlParamValues> {
    const { filterFormValues, genderOptions } = event;
    const urlObj: { [key: string]: string; } = {};
    urlObj[UrlQueryParams.STATE] = `${URIPrefix.STATE}-${filterFormValues.state?.toLocaleLowerCase() ?? EmptyURIValue}`;
    urlObj[UrlQueryParams.CITY] = `${URIPrefix.CITY}-${filterFormValues.city ? fixedEncodeURIComponent(filterFormValues.city).toLocaleLowerCase() : EmptyURIValue}`;
    if (filterFormValues.insurance) {
      urlObj[UrlQueryParams.INSURANCE] = `${URIPrefix.INSURANCE}-${fixedEncodeURIComponent(filterFormValues.insurance.title).toLocaleLowerCase()}`;
    }
    if (filterFormValues?.concerns?.length) {
      const modalities = filterFormValues.concerns.filter((c) => c.isModality || c.isPsychiatrist);
      const specialties = filterFormValues.concerns.filter((c) => !c.isModality && !c.isPsychiatrist);

      if (modalities.length > 0) {
        urlObj[UrlQueryParams.MODALITIES] = modalities.map((m) => `${URIPrefix.MODALITY}-${fixedEncodeURIComponent(m.title)?.toLocaleLowerCase()}`).join(',');
      }
      if (specialties.length > 0) {
        urlObj[UrlQueryParams.SPECIALTIES] = specialties.map((s) => `${URIPrefix.SPECIALTY}-${fixedEncodeURIComponent(s.title)?.toLocaleLowerCase()}`).join(',');
      }
    }
    if (filterFormValues.genders?.length) {
      const genderStrings: string[] = [];
      filterFormValues.genders.forEach((genderId: number) => {
        const gender = genderOptions[0].options.find((option) => option.value === genderId);
        if (gender) {
          genderStrings.push(gender.name?.toLocaleLowerCase());
        }
      });
      urlObj[UrlQueryParams.GENDERS] = genderStrings.map((g) => `${URIPrefix.GENDER}-${fixedEncodeURIComponent(g)}`).join(',');
    }
    return urlObj;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  sendDirectoryPageSegmentEvent(): void {
    combineLatest([
      this.route.paramMap,
      this.route.queryParams
    ])
      .pipe(take(1)).subscribe(([paramMap, queryParams]) => {
        const params = ParseDirectoryFilterParams(paramMap);
        const directoryFilters = {
          state: params[URIPrefix.STATE],
          city: params[URIPrefix.CITY],
          insurance: params[URIPrefix.INSURANCE],
          modalities: params[URIPrefix.MODALITY].join(','),
          specialties: params[URIPrefix.SPECIALTY].join(','),
          genders: params[URIPrefix.GENDER].join(','),
        };
        const referringUrl: string = queryParams['referringUrl'] ?? '';
        // Get UTM parameters
        const utmParams = Object.entries(queryParams).filter(([key, value]) => key.startsWith('utm'));
        let referringDomain: string;
        if (this.document.referrer) {
          referringDomain = new URL(this.document.referrer).origin;
        }
        const segEvent = {
          utm: utmParams,
          count_providers_returned: this.totalProviderCount,
          referring_domain: referringDomain,
          internal_referring_page: referringUrl,
          directory_filters: directoryFilters
        } as ProviderDirectoryPageViewEvent;

        this.segmentService.track<ProviderListingCardEvents.DIRECTORY_PAGE_VIEW>(
          ProviderListingCardEvents.DIRECTORY_PAGE_VIEW, segEvent
        );
      });
  }

  requestAvailabilityForProviders(providerIds: number[]): void {
    // filter out the ids for which we have existing data or placeholders
    const newProvidersAvailabilityState = this.providersAvailabilitySubject.getValue() || {};
    const providerIdsWithoutData = providerIds.filter((providerId) => !newProvidersAvailabilityState[providerId]);

    // create placeholder data
    providerIdsWithoutData.forEach((providerId) => {
      newProvidersAvailabilityState[providerId] = { soonest_availability: null, loading: true };
    });

    // make request sets
    const availabilityRequestSets = this.prepareAvailabilityRequestSets(providerIdsWithoutData);

    // Issue requests for each set of provider IDs
    availabilityRequestSets.forEach((providerIdSet) => {
      this.requestSoonestAvailableSessions$(providerIdSet).pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          // Get the current state of all provider availability data from the subject
          const allProvidersAvailability = this.providersAvailabilitySubject.getValue();

          // for each provider listing in the response
          response.forEach((listing: IProviderSoonestAvailabilityApiListing) => {
            // get the provider id from the listing
            const providerId = listing.persona_provider_id;

            // find the provider in the current provider availability data
            // and overwrite it with a ui representation of this data
            allProvidersAvailability[providerId] = {
              soonest_availability: listing.soonest_availability,
              loading: false
            };
          });

          // Push the updated state to the subject so the UI reflects the changes
          this.providersAvailabilitySubject.next(allProvidersAvailability);
        });
    });
  }

  prepareAvailabilityRequestSets(providerIds: number[]): number[][] {
    // Initialize an empty array to hold batches of provider IDs that will be requested.
    const requestSets: number[][] = [];

    for (let i = 0; i < providerIds.length; i += SOONEST_AVAILABILITY_REQUEST_LIMIT_COUNT) {
      requestSets.push(providerIds.slice(i, i + SOONEST_AVAILABILITY_REQUEST_LIMIT_COUNT));
    }

    return requestSets;
  }

  requestSoonestAvailableSessions$(providerIds: number[]): Observable<IProviderSoonestAvailabilityApiListing[]> {
    return this.providerDirectoryHttpService
      .getSoonestAvailableSessions$(providerIds)
      .pipe(
        map((res: IAPIListResponse<IProviderSoonestAvailabilityApiListing>) => res.data)
      );
  }
}
